import React from 'react';
import { 
  Box, 
  Button, 
  Container, 
  Typography, 
  Card, 
  CardContent,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  useTheme
} from '@mui/material';
import { motion } from 'framer-motion';
import { Link as RouterLink } from 'react-router-dom';
import SEO from '../utils/seo/SEO';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import BusinessCenterIcon from '@mui/icons-material/BusinessCenter';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import PsychologyIcon from '@mui/icons-material/Psychology';
import EngineeringIcon from '@mui/icons-material/Engineering';
import SpeedIcon from '@mui/icons-material/Speed';
import GroupsIcon from '@mui/icons-material/Groups';
import EqualizerIcon from '@mui/icons-material/Equalizer';
import InsightsIcon from '@mui/icons-material/Insights';
import MergeTypeIcon from '@mui/icons-material/MergeType';
import AssessmentIcon from '@mui/icons-material/Assessment';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import Grid from '../utils/GridWrapper';

// Custom SVG Icons
const FractionalCPOIcon = () => (
  <svg width="36" height="36" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="8" y="16" width="48" height="32" rx="4" fill="white"/>
    <path d="M20 28H16V40H20V28Z" fill="white"/>
    <path d="M30 24H26V40H30V24Z" fill="white"/>
    <path d="M40 32H36V40H40V32Z" fill="white"/>
    <path d="M50 20H46V40H50V20Z" fill="white"/>
    <path d="M24 48H40C42.2091 48 44 46.2091 44 44V20C44 17.7909 42.2091 16 40 16H24C21.7909 16 20 17.7909 20 20V44C20 46.2091 21.7909 48 24 48Z" stroke="white" strokeWidth="2"/>
  </svg>
);

const CLevelStrategyIcon = () => (
  <svg width="36" height="36" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M32 12L44 24M32 12L20 24M32 12V40M44 24H52M20 24H12M16 40C16 44.4183 23.1634 48 32 48C40.8366 48 48 44.4183 48 40" stroke="white" strokeWidth="3" strokeLinecap="round"/>
    <path d="M16 32C16 36.4183 23.1634 40 32 40C40.8366 40 48 36.4183 48 32" stroke="white" strokeWidth="3" strokeLinecap="round"/>
  </svg>
);

const AcquisitionPrepIcon = () => (
  <svg width="36" height="36" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="8" y="14" width="48" height="36" rx="4" fill="#0A1E53" fillOpacity="0.5"/>
    <rect x="16" y="22" width="32" height="20" rx="2" fill="white"/>
    <path d="M24 30H22V38H24V30Z" fill="#0A1E53"/>
    <path d="M30 26H28V38H30V26Z" fill="#0A1E53"/>
    <path d="M36 32H34V38H36V32Z" fill="#0A1E53"/>
    <path d="M42 28H40V38H42V28Z" fill="#0A1E53"/>
  </svg>
);

const ProductOpsIcon = () => (
  <svg width="36" height="36" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="32" cy="24" r="12" fill="white" fillOpacity="0.2"/>
    <circle cx="32" cy="24" r="6" fill="white"/>
    <path d="M32 36C22.0589 36 14 44.0589 14 54H50C50 44.0589 41.9411 36 32 36Z" fill="white"/>
    <circle cx="48" cy="24" r="4" fill="white"/>
    <circle cx="48" cy="40" r="4" fill="white"/>
    <circle cx="16" cy="40" r="4" fill="white"/>
    <path d="M48 28V36" stroke="white" strokeWidth="2"/>
    <path d="M16 36V44" stroke="white" strokeWidth="2"/>
    <path d="M44 40H52" stroke="white" strokeWidth="2"/>
    <path d="M12 40H20" stroke="white" strokeWidth="2"/>
  </svg>
);

// Animation variants
const fadeIn = {
  hidden: { opacity: 0, y: 20 },
  visible: { 
    opacity: 1, 
    y: 0,
    transition: { duration: 0.6 }
  }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.2
    }
  }
};

const Services: React.FC = () => {
  const theme = useTheme();

  // Detailed services data
  const services = [
    {
      id: 'fractional-cpo',
      title: 'Fractional CPO Services',
      icon: <FractionalCPOIcon />,
      description: 'Experienced executive product leadership without the full-time commitment, providing strategic direction and operational expertise.',
      details: [
        'Strategic Product Vision & Executive Alignment',
        'Board & C-Level Advisory Services',
        'Product Portfolio Strategy & Governance'
      ]
    },
    {
      id: 'c-level-strategy',
      title: 'C-Level Strategy Consulting',
      icon: <CLevelStrategyIcon />,
      description: 'Comprehensive strategic planning and guidance for executive leadership to drive business transformation and growth.',
      details: [
        'Executive Growth & Market Positioning Strategy',
        'Business Model Innovation & Competitive Analysis',
        'Strategic Partnership & Alliance Development'
      ]
    },
    {
      id: 'acquisition-preparation',
      title: 'Acquisition Preparation',
      icon: <AcquisitionPrepIcon />,
      description: 'Expert guidance to prepare your company for acquisition, with a proven track record of two successful exits.',
      details: [
        'Exit Strategy Development & Valuation Enhancement',
        'Technical & Product Due Diligence Preparation',
        'Strategic Buyer Identification & Negotiation Support'
      ]
    },
    {
      id: 'product-operations',
      title: 'Product Operations Excellence',
      icon: <ProductOpsIcon />,
      description: 'Optimize product development processes and operational workflows with Generative AI integration to enhance quality, efficiency, and market responsiveness.',
      details: [
        'Product Strategy & Innovation Framework Design',
        'AI-Powered Process Optimization & Automation',
        'Data-Driven Decision Support Systems'
      ]
    }
  ];

  // Additional specialized services
  const specializedServices = [
    {
      title: 'Generative AI Integration',
      icon: <PsychologyIcon />,
      description: 'Strategic implementation of Generative AI across your product lifecycle and business operations to drive innovation, efficiency, and competitive advantage.'
    },
    {
      title: 'Product Portfolio Optimization',
      icon: <SpeedIcon />,
      description: 'Strategic evaluation and restructuring of product portfolios to maximize business value and market positioning.'
    },
    {
      title: 'Executive Team Development',
      icon: <GroupsIcon />,
      description: 'Build high-performing executive and product leadership teams with effective structures, skills, and collaborative processes.'
    },
    {
      title: 'Acquisition Due Diligence',
      icon: <AssessmentIcon />,
      description: 'Comprehensive assessment and preparation of all product and technical aspects required for successful M&A due diligence.'
    }
  ];

  // Acquisition success cases
  const acquisitionSuccesses = [
    {
      company: "AccessData Acquisition by Exterro",
      description: "Successful preparation and navigation of company acquisition through comprehensive due diligence support, product strategy alignment, and technical documentation, resulting in optimal valuation.",
      services: [
        'Product roadmap refinement',
        'Operational Efficiency',
        'New Product Innovation',
        'Technical Teams and Engineering team structure optimization',
        'Due diligence coordination'
      ]
    },
    {
      company: "NetWitness Acquisition by PartnerOne",
      description: "Guided executive team through acquisition preparation, including strategic product positioning, operational streamlining, and valuation enhancement, resulting in a successful exit at premium valuation.",
      services: [
        'Technical Teams and Engineering team structure optimization',
        'Product portfolio rationalization',
        'Strategic partnership development',
        'New Product Innovation',
        'Due diligence support'
      ]
    }
  ];

  return (
    <>
      <SEO 
        title="C-Level Strategy, AI Integration & Fractional CPO Services | Ewasko Consulting"
        description="Expert fractional CPO services, C-level strategy consulting, Generative AI integration, and acquisition preparation with a proven track record of successful exits."
        keywords="fractional CPO, C-level strategy, Generative AI integration, AI product strategy, executive consulting, product leadership, exit strategy"
        canonicalUrl="https://todfather.com/services"
      />
      <Box>
        {/* Hero Section */}
        <Box 
          sx={{
            bgcolor: 'primary.main',
            color: 'white',
            py: { xs: 8, md: 12 },
            position: 'relative',
            overflow: 'hidden'
          }}
        >
          <Container maxWidth="lg">
            <Grid container spacing={4} alignItems="center">
              <Grid item xs={12} md={8}>
                <motion.div
                  initial="hidden"
                  animate="visible"
                  variants={fadeIn}
                >
                  <Typography 
                    variant="overline" 
                    component="div" 
                    sx={{ opacity: 0.8, fontWeight: 'bold', letterSpacing: 2 }}
                  >
                    Our Services
                  </Typography>
                  <Typography 
                    variant="h2" 
                    component="h1"
                    gutterBottom
                    sx={{ fontWeight: 'bold' }}
                  >
                    Executive Product Leadership
                  </Typography>
                  <Typography 
                    variant="h5" 
                    sx={{ 
                      mb: 2, 
                      maxWidth: '800px',
                      opacity: 0.9
                    }}
                  >
                    Strategic fractional CPO services, C-level consulting, and acquisition preparation with a proven record of successful exits.
                  </Typography>
                  <Typography 
                    variant="body1" 
                    sx={{ 
                      mb: 4, 
                      maxWidth: '800px',
                      opacity: 0.9,
                      fontStyle: 'italic',
                      lineHeight: 1.6
                    }}
                  >
                    "I provide on-demand, C-level product leadership—aligning strategic vision with execution, optimizing operations for rapid innovation, and positioning companies for premium-value exits—all without the overhead of a full-time hire."
                  </Typography>
                  <Button 
                    variant="contained" 
                    color="secondary"
                    size="large"
                    component={RouterLink}
                    to="/contact"
                    sx={{ px: 4, py: 1.5 }}
                  >
                    Request a Consultation
                  </Button>
                </motion.div>
              </Grid>
            </Grid>
          </Container>
        </Box>

        {/* Main Services Section */}
        <Box sx={{ py: { xs: 6, md: 8 } }}>
          <Container maxWidth="lg">
            <Box sx={{ mb: 6, textAlign: 'center' }}>
              <Typography variant="h3" component="h2" color="primary" gutterBottom>
                Core Executive Services
              </Typography>
              <Typography variant="body1" color="textSecondary" sx={{ maxWidth: '800px', mx: 'auto' }}>
                Leveraging 20+ years of executive product leadership experience to deliver high-impact C-level consulting services for transformative business outcomes.
              </Typography>
            </Box>

            {services.map((service, index) => (
              <Box 
                key={service.id}
                component={motion.div}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeIn}
                sx={{ 
                  mb: 6,
                  borderRadius: 4,
                  overflow: 'hidden',
                  boxShadow: '0 10px 30px rgba(0,0,0,0.05)',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-5px)',
                    boxShadow: '0 15px 35px rgba(0,0,0,0.1)'
                  }
                }}
                id={service.id}
              >
                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' } }}>
                  {/* Icon Column */}
                  <Box
                    sx={{
                      width: { xs: '100%', md: '100px' },
                      minWidth: { xs: '100%', md: '100px' },
                      background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      py: { xs: 3, md: 0 }
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      {service.icon}
                    </Box>
                  </Box>
                  
                  {/* Content Column */}
                  <Box 
                    sx={{ 
                      flexGrow: 1,
                      p: 4,
                      backgroundColor: 'background.paper'
                    }}
                  >
                    <Typography 
                      variant="h4" 
                      component="h3" 
                      gutterBottom 
                      color="primary.main"
                      sx={{ fontWeight: 'bold' }}
                    >
                      {service.title}
                    </Typography>
                    <Typography variant="body1" paragraph sx={{ mb: 3 }}>
                      {service.description}
                    </Typography>
                    <Box
                      sx={{
                        backgroundColor: 'background.default',
                        borderRadius: 2,
                        p: 2,
                        border: '1px solid rgba(0,0,0,0.05)',
                      }}
                    >
                      <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600, color: theme.palette.primary.main }}>
                        What we offer:
                      </Typography>
                      <List disablePadding>
                        {service.details.map((detail, idx) => (
                          <ListItem 
                            key={idx} 
                            sx={{ 
                              py: 0.5,
                              px: 0,
                              '&:not(:last-child)': {
                                mb: 1
                              }
                            }}
                          >
                            <ListItemIcon sx={{ minWidth: 36 }}>
                              <CheckCircleOutlineIcon color="primary" sx={{ fontSize: 20 }} />
                            </ListItemIcon>
                            <ListItemText 
                              primary={detail} 
                              primaryTypographyProps={{ 
                                variant: 'body2', 
                                fontWeight: 500
                              }} 
                            />
                          </ListItem>
                        ))}
                      </List>
                    </Box>
                  </Box>
                </Box>
              </Box>
            ))}
          </Container>
        </Box>

        {/* Acquisition Success Section */}
        <Box sx={{ py: { xs: 6, md: 8 }, bgcolor: 'background.paper' }}>
          <Container maxWidth="lg">
            <Box sx={{ textAlign: 'center', mb: 5 }}>
              <Typography 
                variant="overline" 
                component="div" 
                color="primary" 
                fontWeight="bold"
                sx={{ letterSpacing: 2 }}
              >
                PROVEN SUCCESS
              </Typography>
              <Typography variant="h3" component="h2" gutterBottom>
                Successful Exit Strategy Cases
              </Typography>
              <Typography variant="body1" color="textSecondary" sx={{ maxWidth: '800px', mx: 'auto', mb: 4 }}>
                Our proven track record includes guiding multiple companies through successful acquisitions, ensuring optimal valuations and smooth transitions.
              </Typography>
            </Box>
            
            <Grid container spacing={4} justifyContent="center">
              {acquisitionSuccesses.map((success, index) => (
                <Grid item xs={12} md={6} key={index}>
                  <motion.div
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true }}
                    variants={fadeIn}
                  >
                    <Card 
                      sx={{ 
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        borderTop: `4px solid ${theme.palette.primary.main}`,
                        transition: 'all 0.3s ease',
                        '&:hover': { 
                          transform: 'translateY(-8px)',
                          boxShadow: theme.shadows[8]
                        }
                      }}
                      elevation={3}
                    >
                      <CardContent sx={{ p: 3, flexGrow: 1 }}>
                        <Typography variant="h5" component="h3" gutterBottom sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                          {success.company}
                        </Typography>
                        <Typography variant="body1" paragraph>
                          {success.description}
                        </Typography>
                        <Divider sx={{ my: 2 }} />
                        <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 'bold' }}>
                          Key Services Provided:
                        </Typography>
                        <List dense>
                          {success.services.map((service, idx) => (
                            <ListItem key={idx} sx={{ py: 0.3 }}>
                              <ListItemIcon>
                                <CheckCircleOutlineIcon color="primary" />
                              </ListItemIcon>
                              <ListItemText primary={service} />
                            </ListItem>
                          ))}
                        </List>
                      </CardContent>
                    </Card>
                  </motion.div>
                </Grid>
              ))}
            </Grid>
          </Container>
        </Box>

        {/* Specialized Services */}
        <Box sx={{ py: { xs: 6, md: 8 }, bgcolor: 'background.default' }}>
          <Container maxWidth="lg">
            <Box sx={{ textAlign: 'center', mb: 5 }}>
              <Typography variant="h3" component="h2" gutterBottom>
                Specialized Executive Offerings
              </Typography>
              <Typography variant="body1" color="textSecondary" sx={{ maxWidth: '800px', mx: 'auto' }}>
                In addition to our core services, we provide specialized executive consulting solutions to address strategic business challenges and opportunities.
              </Typography>
            </Box>
            
            <motion.div 
              variants={staggerContainer}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.2 }}
            >
              <Grid container spacing={3}>
                {specializedServices.map((service, index) => (
                  <Grid item xs={12} md={6} key={index}>
                    <motion.div variants={fadeIn}>
                      <Card 
                        sx={{ 
                          height: '100%', 
                          display: 'flex',
                          transition: 'all 0.3s ease',
                          '&:hover': { 
                            transform: 'translateY(-8px)',
                            boxShadow: theme.shadows[10]
                          }
                        }}
                        elevation={2}
                      >
                        <CardContent sx={{ p: 3, flexGrow: 1 }}>
                          <Box sx={{ display: 'flex', mb: 1.5, alignItems: 'center' }}>
                            <Box 
                              sx={{ 
                                color: 'primary.main',
                                mr: 1.5,
                                display: 'flex',
                                alignItems: 'center'
                              }}
                            >
                              {service.icon}
                            </Box>
                            <Typography variant="h5" component="h3" sx={{ fontWeight: 'bold' }}>
                              {service.title}
                            </Typography>
                          </Box>
                          <Typography variant="body1">
                            {service.description}
                          </Typography>
                        </CardContent>
                      </Card>
                    </motion.div>
                  </Grid>
                ))}
              </Grid>
            </motion.div>
          </Container>
        </Box>
        
        {/* CTA Section */}
        <Box sx={{ py: { xs: 6, md: 8 }, textAlign: 'center' }}>
          <Container maxWidth="md">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeIn}
            >
              <Typography variant="h3" component="h2" gutterBottom>
                Ready for Executive Guidance?
              </Typography>
              <Typography variant="body1" paragraph color="textSecondary" sx={{ mb: 3, maxWidth: '700px', mx: 'auto' }}>
                Contact us today to discuss how our fractional CPO services and strategic consulting can help your business achieve its growth objectives or prepare for a successful exit.
              </Typography>
              <Button 
                variant="contained" 
                color="primary" 
                size="large"
                component={RouterLink}
                to="/contact"
                sx={{ py: 1.5, px: 4 }}
              >
                Get Started
              </Button>
            </motion.div>
          </Container>
        </Box>
      </Box>
    </>
  );
};

export default Services;